import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";

export interface Estoque {
  id: string;
  user_id: string;
  item: string;
  codigo: string;
  categoria: string;
  estoque: number;
  minimo: number;
  maximo: number;
  valor_unitario: number;
  observacoes?: string;
  created_at: string;
  updated_at: string;
}

export interface EstoqueInput {
  item: string;
  codigo: string;
  categoria: string;
  estoque: number;
  minimo: number;
  maximo: number;
  valor_unitario: number;
  observacoes?: string;
}

export function useEstoque() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: estoques = [], isLoading } = useQuery({
    queryKey: ["estoque", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const data = await apiList<Estoque>("estoque");
      return data.sort((a, b) => a.item.localeCompare(b.item));
    },
    enabled: !!user?.id,
  });

  const createEstoque = useMutation({
    mutationFn: async (input: EstoqueInput) => {
      if (!user?.id) throw new Error("User not authenticated");

      return apiCreate<Estoque>("estoque", input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estoque"] });
      toast({
        title: "Item cadastrado",
        description: "O item foi cadastrado com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao cadastrar",
        description: "Não foi possível cadastrar o item.",
        variant: "destructive",
      });
    },
  });

  const updateEstoque = useMutation({
    mutationFn: async ({ id, ...input }: EstoqueInput & { id: string }) => {
      return apiUpdate<Estoque>("estoque", id, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estoque"] });
      toast({
        title: "Item atualizado",
        description: "O item foi atualizado com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o item.",
        variant: "destructive",
      });
    },
  });

  const deleteEstoque = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("estoque", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estoque"] });
      toast({
        title: "Item excluído",
        description: "O item foi excluído com sucesso.",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao excluir",
        description: "Não foi possível excluir o item.",
        variant: "destructive",
      });
    },
  });

  return {
    estoques,
    isLoading,
    createEstoque,
    updateEstoque,
    deleteEstoque,
  };
}
